import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip,
  Paper,
  Menu,
  MenuItem,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  InsertDriveFile as GoogleDocsIcon,
  GridOn as GoogleSheetsIcon,
  Folder as GoogleDriveIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const CommentDialog = ({ open, onClose, task, onUpdateTask }) => {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [googleDriveLink, setGoogleDriveLink] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);

  // Available assignees for mentions
  const assignees = [
    { id: 'installer1', name: 'John Smith', avatar: 'J', color: '#1976d2' },
    { id: 'installer2', name: 'Mike Johnson', avatar: 'M', color: '#388e3c' },
    { id: 'installer3', name: 'Sarah Williams', avatar: 'S', color: '#7b1fa2' },
    { id: 'lead1', name: 'Tom Davis', avatar: 'T', color: '#d32f2f' },
  ];

  useEffect(() => {
    if (task?.comments && Array.isArray(task.comments)) {
      setComments(task.comments);
    } else {
      setComments([]);
    }
  }, [task]);

  const handleAddComment = () => {
    if (!newComment.trim()) return;

    const comment = {
      id: `comment-${Date.now()}`,
      text: newComment,
      author: assignees[0], // Mock current user
      timestamp: new Date().toISOString(),
      replyTo: replyingTo,
      attachments: attachmentPreview ? [attachmentPreview] : [],
      edited: false,
    };

    const updatedComments = [...comments, comment];
    setComments(updatedComments);

    if (onUpdateTask) {
      onUpdateTask({
        ...task,
        comments: updatedComments,
        commentCount: updatedComments.length,
      });
    }

    setNewComment('');
    setReplyingTo(null);
    setAttachmentPreview(null);
  };

  const handleEditComment = (commentId) => {
    const comment = comments.find(c => c.id === commentId);
    if (comment) {
      setEditingComment(commentId);
      setEditText(comment.text);
      handleCloseMenu();
    }
  };

  const handleSaveEdit = () => {
    const updatedComments = comments.map(comment =>
      comment.id === editingComment
        ? { ...comment, text: editText, edited: true, editedAt: new Date().toISOString() }
        : comment
    );

    setComments(updatedComments);
    if (onUpdateTask) {
      onUpdateTask({
        ...task,
        comments: updatedComments,
      });
    }

    setEditingComment(null);
    setEditText('');
  };

  const handleDeleteComment = (commentId) => {
    const updatedComments = comments.filter(c => c.id !== commentId);
    setComments(updatedComments);

    if (onUpdateTask) {
      onUpdateTask({
        ...task,
        comments: updatedComments,
        commentCount: updatedComments.length,
      });
    }

    handleCloseMenu();
  };

  const handleReply = (comment) => {
    setReplyingTo(comment);
    setNewComment(`@${comment.author.name} `);
  };

  const handleOpenMenu = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  const handleFileAttach = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachmentPreview({
          name: file.name,
          type: file.type,
          url: e.target.result,
          isGoogleDrive: false,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddGoogleDriveLink = () => {
    if (googleDriveLink && linkTitle) {
      // Determine the type of Google Drive link
      let linkType = 'google-drive';
      if (googleDriveLink.includes('docs.google.com/document')) {
        linkType = 'google-docs';
      } else if (googleDriveLink.includes('docs.google.com/spreadsheets')) {
        linkType = 'google-sheets';
      } else if (googleDriveLink.includes('docs.google.com/presentation')) {
        linkType = 'google-slides';
      }

      setAttachmentPreview({
        name: linkTitle,
        type: linkType,
        url: googleDriveLink,
        isGoogleDrive: true,
      });

      // Clear the input
      setGoogleDriveLink('');
      setLinkTitle('');
      setShowLinkInput(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return format(date, 'MMM d, yyyy');
  };

  const renderComment = (comment, isReply = false) => (
    <ListItem
      key={comment.id}
      alignItems="flex-start"
      sx={{
        pl: isReply ? 6 : 2,
        opacity: editingComment === comment.id ? 0.7 : 1,
        bgcolor: editingComment === comment.id ? 'action.hover' : 'transparent',
        borderRadius: 1,
        mb: 1,
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: comment.author.color, width: 36, height: 36 }}>
          {comment.author.avatar}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {comment.author.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(comment.timestamp)}
            </Typography>
            {comment.edited && (
              <Chip
                label="edited"
                size="small"
                sx={{ height: 16, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        }
        secondary={
          editingComment === comment.id ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                size="small"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveEdit}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingComment(null);
                    setEditText('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              {comment.replyTo && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    bgcolor: 'action.hover',
                    p: 0.5,
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  Replying to @{comment.replyTo.author.name}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {comment.text}
              </Typography>
              {comment.attachments?.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {comment.attachments.map((attachment, idx) => {
                    let icon = <AttachFileIcon />;
                    if (attachment.type?.startsWith('image/')) icon = <ImageIcon />;
                    else if (attachment.type === 'google-docs') icon = <GoogleDocsIcon />;
                    else if (attachment.type === 'google-sheets') icon = <GoogleSheetsIcon />;
                    else if (attachment.type === 'google-drive') icon = <GoogleDriveIcon />;

                    return (
                      <Chip
                        key={idx}
                        label={attachment.name}
                        size="small"
                        icon={icon}
                        onClick={() => window.open(attachment.url, '_blank')}
                        deleteIcon={attachment.isGoogleDrive ? <OpenInNewIcon /> : undefined}
                        onDelete={attachment.isGoogleDrive ? () => window.open(attachment.url, '_blank') : undefined}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )
        }
      />

      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleReply(comment)}
            disabled={editingComment !== null}
          >
            <ReplyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleOpenMenu(e, comment)}
            disabled={editingComment !== null}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );

  const getReplies = (commentId) => {
    return comments.filter(c => c.replyTo?.id === commentId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Comments</Typography>
          <Typography variant="body2" color="text.secondary">
            {task?.title || 'Task'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
        {comments.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment!
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {comments && Array.isArray(comments) && comments.filter(c => !c.replyTo).map(comment => (
              <React.Fragment key={comment.id}>
                {renderComment(comment)}
                {getReplies(comment.id).map(reply => renderComment(reply, true))}
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <Divider />

      <Box sx={{ p: 2 }}>
        {replyingTo && (
          <Paper
            variant="outlined"
            sx={{ p: 1, mb: 1, bgcolor: 'action.hover' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption">
                Replying to <strong>@{replyingTo.author.name}</strong>
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setReplyingTo(null);
                  setNewComment('');
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        {attachmentPreview && (
          <Paper
            variant="outlined"
            sx={{ p: 1, mb: 1, bgcolor: 'action.hover' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Chip
                icon={
                  attachmentPreview.type === 'google-docs' ? <GoogleDocsIcon /> :
                  attachmentPreview.type === 'google-sheets' ? <GoogleSheetsIcon /> :
                  attachmentPreview.type === 'google-drive' ? <GoogleDriveIcon /> :
                  attachmentPreview.type?.startsWith('image/') ? <ImageIcon /> :
                  <AttachFileIcon />
                }
                label={attachmentPreview.name}
                size="small"
                deleteIcon={attachmentPreview.isGoogleDrive ? <OpenInNewIcon /> : undefined}
              />
              <IconButton
                size="small"
                onClick={() => setAttachmentPreview(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Avatar sx={{ bgcolor: assignees[0].color, width: 32, height: 32 }}>
            {assignees[0].avatar}
          </Avatar>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
            sx={{ flexGrow: 1 }}
          />

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => setAttachMenuAnchor(e.currentTarget)}
            >
              <AttachFileIcon />
            </IconButton>
            <input
              type="file"
              id="comment-file-attach"
              style={{ display: 'none' }}
              onChange={handleFileAttach}
            />

            <IconButton
              color="primary"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleEditComment(selectedComment?.id)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteComment(selectedComment?.id)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Attachment Type Menu */}
      <Menu
        anchorEl={attachMenuAnchor}
        open={Boolean(attachMenuAnchor)}
        onClose={() => setAttachMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          document.getElementById('comment-file-attach').click();
          setAttachMenuAnchor(null);
        }}>
          <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
          Upload File
        </MenuItem>
        <MenuItem onClick={() => {
          setShowLinkInput(true);
          setAttachMenuAnchor(null);
        }}>
          <LinkIcon fontSize="small" sx={{ mr: 1 }} />
          Add Google Drive Link
        </MenuItem>
      </Menu>

      {/* Google Drive Link Input Dialog */}
      <Dialog
        open={showLinkInput}
        onClose={() => {
          setShowLinkInput(false);
          setGoogleDriveLink('');
          setLinkTitle('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Google Drive Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Document Title"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g. Floor Plans v3"
          />
          <TextField
            fullWidth
            label="Google Drive URL"
            value={googleDriveLink}
            onChange={(e) => setGoogleDriveLink(e.target.value)}
            placeholder="https://docs.google.com/..."
            helperText="Paste the sharing link from Google Drive, Docs, Sheets, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowLinkInput(false);
            setGoogleDriveLink('');
            setLinkTitle('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGoogleDriveLink}
            variant="contained"
            disabled={!googleDriveLink || !linkTitle}
          >
            Add Link
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CommentDialog;